import { useEffect, useState } from "react";
import type { ArenaState } from "@shared/types";

interface TutorialOverlayProps {
  state: ArenaState;
}

const CONTROLS: { keys: string[]; label: string }[] = [
  { keys: ["W", "A", "S", "D"], label: "Move (or arrow keys)" },
  { keys: ["Click"], label: "Shoot at the crosshair" },
  { keys: ["Space"], label: "Shoot (alternative)" },
  { keys: ["E"], label: "Upgrade shop" },
];

export function TutorialOverlay({ state }: TutorialOverlayProps) {
  const [dismissed, setDismissed] = useState(false);
  const [show, setShow] = useState(false);

  // Show once the battle is active and the player hasn't dismissed it.
  useEffect(() => {
    if (state.phase === "active" && !dismissed) setShow(true);
  }, [state.phase, dismissed]);

  // Auto-hide after a while so it doesn't linger over the action.
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => setDismissed(true), 9000);
    return () => clearTimeout(t);
  }, [show]);

  if (!show || dismissed) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
      <div className="pointer-events-auto mx-4 w-full max-w-md rounded-2xl border border-archer-700 bg-archer-900/95 p-6 shadow-2xl backdrop-blur">
        <div className="mb-1 text-center text-2xl font-black tracking-tight text-white">
          How to Play
        </div>
        <p className="mb-5 text-center text-sm text-archer-300">
          Out-arrow the enemy faction. Your side wins on score when the timer runs out.
        </p>

        {/* Controls */}
        <div className="mb-5 space-y-2">
          {CONTROLS.map((c) => (
            <div key={c.label} className="flex items-center gap-3">
              <div className="flex w-28 shrink-0 flex-wrap gap-1">
                {c.keys.map((k) => (
                  <kbd
                    key={k}
                    className="rounded-md border border-archer-700 bg-archer-800 px-2 py-0.5 text-xs font-bold text-white"
                  >
                    {k}
                  </kbd>
                ))}
              </div>
              <span className="text-sm text-archer-300">{c.label}</span>
            </div>
          ))}
        </div>

        {/* Objectives */}
        <div className="mb-5 space-y-1.5 rounded-xl border border-archer-700 bg-archer-950/60 p-3 text-sm text-archer-300">
          <div>
            <span className="font-bold text-dawn-400">☀️ / 🌙</span> Kill enemy creatures &
            archers to score.
          </div>
          <div>
            <span className="font-bold text-dusk-400">🏹</span> Aim with the crosshair — arrows
            home in on targets under it.
          </div>
          <div>
            <span className="font-bold text-white">✨</span> Earn fame from kills, spend it in the
            shop (<kbd className="rounded bg-archer-800 px-1 text-xs">E</kbd>) to grow stronger.
          </div>
          <div>
            <span className="font-bold text-red-400">❤️</span> Don't let your HP hit 0 — you
            respawn in 3s.
          </div>
        </div>

        <button
          onClick={() => setDismissed(true)}
          className="w-full rounded-xl bg-gradient-to-r from-dawn-600 to-dusk-600 px-6 py-3 font-bold text-white transition-all hover:brightness-110"
        >
          Got it — let's fight!
        </button>
      </div>
    </div>
  );
}
